import { getStringAnchorProperties, getStringButtonProperties, removeLoadSpinner, IDataItem, ISettings } from './rms-ui.navi';

/** 
 * ======================================================================== 
 * RMS G-Navi
 * ======================================================================== 
 */
let timeout: NodeJS.Timeout | null;

/** Initialize G-Navi module (add toggler) */
export function initGNaviModule(settings: ISettings, target: string) {
  const { classNames } = settings.gnavi;
  const gnavi = document.querySelector(target as string);
  for (const className of classNames) if (!gnavi.classList.contains(className)) gnavi.classList.add(className);
  renderGNaviToggler(settings, gnavi as HTMLElement);
}

/** Initialize Error for G-Navi module */
export function initGNaviError(settings: ISettings) {
  const { target, classDataError, labelDataError} = settings.gnavi;
  const gnavi = document.querySelector(target as string);
  const label = document.createElement('span');
  label.classList.add(classDataError);
  label.innerHTML = labelDataError;
  gnavi.appendChild(label);
}

/** Renders the G-Navi module */
export function renderGNavi(settings: ISettings, data?: IDataItem[]) {
  try {
    const { target } = settings.gnavi as IGNavi;
    if (!target) return;
    const gnavi = document.querySelector(target as string);
    if (gnavi) removeLoadSpinner(settings as ISettings, gnavi);
    if (!data) return initGNaviError(settings);
    renderGNaviContentCategories(settings, data, gnavi as HTMLElement);
    renderGNaviBackdrop(settings, gnavi as HTMLElement);
    renderGNaviBackdropButton(settings, gnavi as HTMLElement);
    showTooltips(settings, gnavi as HTMLElement);
  } catch (error) {
    throw error;
  }
}

/** Renders Categories */
function renderGNaviContentCategories(settings: ISettings, categories: IDataItem[], parentElement: HTMLElement) {
  const { classContentCategories, classDisabled } = settings.gnavi as IGNavi;
  const contentCategories = document.createElement('nav');
  contentCategories.className = classContentCategories;
  const ul = document.createElement('ul');
  parentElement.appendChild(contentCategories);
  renderGNaviContentCategoryItems(settings, categories, ul);
  contentCategories.appendChild(ul);
  const buttons = parentElement.querySelectorAll(
    `.${classContentCategories} > ul > li > a:not(.${classDisabled}):not([href]),` +
    `.${classContentCategories} > ul > li > button:not(.${classDisabled})`
  );
  if (!buttons.length) return;
  buttons.forEach(button => {
    button.addEventListener('click', handlerGNaviCategoryOnClick(settings, parentElement));
  });
}

/** Renders Category items */
function renderGNaviContentCategoryItems(settings: ISettings, categories: IDataItem[], parentElement: HTMLElement) {
  const { classIcon, classActive, classDisabled, classContentCategoryItem } = settings.gnavi as IGNavi;
  const { target } = settings;
  let liTemplate = '';
  for (const cat of categories) {
    if (!cat.title) continue;
    let active = false;
    if (cat.list) active = renderGNaviContentSubCategory(settings, cat);
    const attrClassActive = target === cat.id || active ? `class="${classActive}"` : '';
    const anchorClassList: string[] = [];
    !cat.canAccess && anchorClassList.push(classDisabled);
    const anchorClasses = anchorClassList.length ? `class=${anchorClassList.join(' ')}` : '';
    const clickableELementContent = `
      <div class="${classContentCategoryItem}">
        <i class="${classIcon} ${cat.icon}"></i>
        <span>${cat.title.join('<br/>')}</span>
      </div>`;
    const clickableELement = cat.list?.length ? 
      `<button ${getStringButtonProperties(cat)} ${anchorClasses}>${clickableELementContent}</button>` : 
      `<a ${getStringAnchorProperties(cat)} ${anchorClasses}>${clickableELementContent}</a>`;
    liTemplate += `<li data-toggle="gnavi-tooltip" data-original-title="${cat.title.join('')}" ${attrClassActive}>${clickableELement}</li>`;
  }
  parentElement.innerHTML = liTemplate;
}

/** Renders Subcategory */
function renderGNaviContentSubCategory(settings: ISettings, category: IDataItem): boolean {
  const { target, classContentSubCategories, classContentSubcategory, classContentSubcategoryOpen } = settings.gnavi as IGNavi;
  if (!category.list) return false;
  const gnavi = document.querySelector(`${target}`);
  let contentSubCategories = gnavi?.querySelector(`.${classContentSubCategories}`);
  let active = false;
  if (!contentSubCategories) {
    contentSubCategories = document.createElement('nav');
    contentSubCategories.className = classContentSubCategories;
  }
  const ul = document.createElement('ul');
  const classes: string[] = [classContentSubcategory];
  ul.dataset.gnaviCategoryTarget = `#${category.id}`;
  category.list.map((item, index) => {
    if (renderGNaviContentSubCategoryItem(settings, ul, item, index + 1)) active = true;
  }).join('');
  if (active) classes.push(classContentSubcategoryOpen);
  ul.className = classes.join(' ');
  contentSubCategories.appendChild(ul);
  gnavi?.appendChild(contentSubCategories);
  return active;
}

/** Render Subcategory item */
function renderGNaviContentSubCategoryItem(settings: ISettings, parentElement: Element, item: IDataItem, index?: number): boolean {
  const {
    target,
    classDisabled,
    classContentSubcategoryAccordion,
    classContentCategoryItem,
    classContentSubcategoryAccordionToggle,
    classContentSubcategoryItem,
    classContentSubcategoryItemNumber,
    classContentSubcategoryAccordionMenu,
    classContentSubcategoryAccordionOpen,
    classActive
  } = settings.gnavi as IGNavi;
  if (!parentElement || !item.title) return false;
  const classes: string[] = [];
  let active = item.id === settings.target;
  let content = '';
  const disabled = !item.canAccess ? item.list?.length ? `disabled="disabled"` : `class="${classDisabled}"` : '';
  if (item.list) {
    classes.push(classContentSubcategoryAccordion);
    const listItemsContent = item.list.map(item => {
      const result = renderGNaviContentSubCategoryAccordionItem(settings, item);
      if (result.active) active = true;
      return result.string;
    }).join('');
    content = `
      <button type="button" ${getStringButtonProperties(item)} class="${classContentSubcategoryAccordionToggle}" data-toggle="accordion" ${disabled}>
        <div class="${classContentSubcategoryItem}">
          <span class="${classContentSubcategoryItemNumber}">${index}</span>
          <span>${item.title.join('<br/>')}</span>
        </div>
      </button>
      <ul class="${classContentSubcategoryAccordionMenu}">
        ${listItemsContent}
      </ul>
    `;
  } else {
    const indexContent = index ? `<span class="${classContentSubcategoryItemNumber}">${index}</span>` : '';
    content = `
      <a ${getStringAnchorProperties(item)} ${disabled}>
        <div class="${classContentCategoryItem}">
          ${indexContent}
          <span>${item.title.join('<br/>')}</span>
        </div>
      </a>
    `;
  }
  if (active) {
    classes.push(`${classContentSubcategoryAccordionOpen} ${classActive}`);
    const parentElement = document.querySelector(target);
    if (parentElement) (parentElement as HTMLElement).dataset.gnaviSecondDirectoryMode = 'true';
  }
  const liElement = document.createElement('li');
  if (classes.length) liElement.className = classes.join(' ');
  liElement.innerHTML = content;
  parentElement.appendChild(liElement);
  if (liElement.classList.contains(classContentSubcategoryAccordion)) attachGNaviHandlerAccordion(settings, liElement);
  return active;
}

/** Renders accordion item inside Subcategory */
function renderGNaviContentSubCategoryAccordionItem(settings: ISettings, item: IDataItem): { string: string, active: boolean } {
  const {
    target,
    classActive,
    classDisabled,
    classContentCategoryItem,
  } = settings.gnavi;
  if (!item || !item.title?.length) return { string: '', active: false };
  const disabled = !item.canAccess ? `class="${classDisabled}"` : '';
  const classes: string[] = [];
  const active = item.id === settings.target;
  if (active) {
    classes.push(classActive);
    const parentElement = document.querySelector(target);
    if (parentElement) (parentElement as HTMLElement).dataset.gnaviSecondDirectoryMode = 'true';
  }
  return { string: `
    <li ${classes.length ? `class="${classes.join(' ')}"` : ''}>
      <a ${getStringAnchorProperties(item)} ${disabled}>
        <div class="${classContentCategoryItem}">
          <span>${item.title.join('<br/>')}</span>
        </div>
      </a>
    </li>
  `, active: active} ;
}

/** Renders Toggler to minimize G-Navi */
function renderGNaviToggler(settings: ISettings, parentElement: HTMLElement) {
  const { classToggler, labelTogglerTooltipTitleOpen, labelTogglerTooltipTitleClosed } = settings.gnavi as IGNavi;
  const toggler = document.createElement('div');
  toggler.className = classToggler;
  const button = document.createElement('button');
  button.type = 'button';
  button.dataset.gnaviToggle = 'button';
  const dataMinimized = parentElement.dataset.gnaviMinimized === 'true';
  button.dataset.originalTitle = dataMinimized ? labelTogglerTooltipTitleClosed : labelTogglerTooltipTitleOpen;
  toggler.appendChild(button);
  parentElement.appendChild(toggler);
  toggleTooltips(settings, parentElement as HTMLElement, TooltipMode.default, `.${classToggler} [data-gnavi-toggle="button"]`);
  attachGNaviHandlerToggler(settings, parentElement);
}

/** Renders Backdrop */
function renderGNaviBackdrop(settings: ISettings, parentElement: HTMLElement) {
  const { classBackdrop } = settings.gnavi as IGNavi;
  const backdrop = document.createElement('div');
  backdrop.className = classBackdrop;
  parentElement.appendChild(backdrop);
  backdrop.addEventListener('click', attachGNaviHandlerBackdropOnClick(settings, parentElement));
}

/** Renders Backdrop button */
function renderGNaviBackdropButton(settings: ISettings, parentElement: HTMLElement) {
  const { classBackdropButton, classBackdropButtonContent, labelBackdropButton } = settings.gnavi as IGNavi;
  // Backdrop Button wrapper
  const backdropButton = document.createElement('div');
  backdropButton.className = classBackdropButton;
  // Button wrapper
  const backdropButtonContent = document.createElement('div');
  backdropButtonContent.className = classBackdropButtonContent;
  // Button
  const backdropButtonContentBtn = document.createElement('button');
  backdropButtonContentBtn.type = 'button';
  backdropButtonContentBtn.dataset.gnaviBackdrop = 'button';
  backdropButtonContentBtn.innerHTML = `<span>${labelBackdropButton}</span>`;

  backdropButtonContent.appendChild(backdropButtonContentBtn);
  backdropButton.appendChild(backdropButtonContent);
  parentElement.appendChild(backdropButton);
  backdropButtonContentBtn.addEventListener('click', attachGNaviHandlerBackdropOnClick(settings, parentElement));
}

/** Attach handler - Handles everything that needs to be done after clicking on the Backdrop */
const attachGNaviHandlerBackdropOnClick = (settings: ISettings, parentElement: Element) => () => {
  const { classContentCategories, classSelected, target,  } = settings.gnavi as IGNavi;
  delete (parentElement as HTMLElement).dataset.gnaviBackdrop;
  delete (parentElement as HTMLElement).dataset.gnaviSelectionMode;
  const selectedCategories = parentElement.querySelectorAll(`.${classContentCategories} > ul > li.${classSelected}`);
  if (selectedCategories) selectedCategories.forEach((cat) => cat.classList.remove(classSelected));
  resetGNaviActiveSubcategory(settings, parentElement);
  detachGNaviHandlerCategory(settings);
  showTooltips(settings, parentElement as HTMLElement);
  const categoryMenuElement = document.querySelector(`${target} .${classContentCategories}`);
  detachGNaviHandlerScroll(parentElement, categoryMenuElement); // Detach Scroll event
  if (categoryMenuElement) {
    (categoryMenuElement as HTMLElement).style.top = null; // Remove top position from Categories Menu
    (categoryMenuElement as HTMLElement).style.left = null; // Remove left position from Categories Menu
  }
};

/** Attach handler - Add click event to expand/collapse accordion */
const attachGNaviHandlerAccordion = (settings: ISettings, parentElement: Element) => {
  const { classContentSubcategoryAccordionToggle } = settings.gnavi as IGNavi;
  const accordionButton = parentElement.querySelector(`.${classContentSubcategoryAccordionToggle}`);
  accordionButton?.addEventListener('click', handlerGNaviAccordionToggleOnClick(settings));
};

/** Attach handler - Adds click event to toggle minimize, adds scroll/resize event to calculate offsets on fixed elements */
const attachGNaviHandlerToggler = (settings: ISettings, parentElement: Element) => {
  const { classToggler } = settings.gnavi as IGNavi;
  const toggler = parentElement.querySelector(`.${classToggler}`);
  toggler?.addEventListener('click', handlerGNaviTogglerOnClick(settings, parentElement as HTMLElement));
  window.addEventListener('scroll', handlerSetLeftPosition(parentElement, toggler));
  window.addEventListener('resize', handlerSetLeftPosition(parentElement, toggler));
};

/** Attach handler - Adds hover/leave events used to select Categories on hover/leave */
function attachGNaviHandlerCategory(settings: ISettings) {
  const { target, classContentCategories, classDisabled } = settings.gnavi as IGNavi;
  const parentElement = document.querySelector(target);
  const categories = parentElement.querySelectorAll(
    `.${classContentCategories} ul li a:not(.${classDisabled}):not([href]),` +
    `.${classContentCategories} ul li button:not(.${classDisabled})`
  );
  const opts: AddEventListenerOptions & EventListenerOptions = { passive: true };
  categories.forEach(cat => { 
    cat.addEventListener('mouseenter', handlerGNaviCategoryOnMouseOver(settings, parentElement), opts);
    cat.addEventListener('mouseleave', handlerGNaviCategoryOnMouseLeave(parentElement), opts);
  });
}

/** Attach handler - Adds scroll/resize events used to calculate offsets on fixed elements */
function attachGNaviHandlerScroll(parentElement: Element, targetElement: Element) {
  const opts: AddEventListenerOptions & EventListenerOptions = { passive: true };
  window.addEventListener('scroll', handlerSetOffsetPosition(parentElement, targetElement), opts);
  window.addEventListener('resize', handlerSetOffsetPosition(parentElement, targetElement), opts);
}

/** Detach handler - Removes hover/leave events used to select Categories */
function detachGNaviHandlerCategory(settings: ISettings) {
  const { target, classContentCategories, classDisabled } = settings.gnavi as IGNavi;
  const parentElement = document.querySelector(target);
  const categories = parentElement.querySelectorAll(
    `.${classContentCategories} ul li a:not(.${classDisabled}):not([href]),`+
    `.${classContentCategories} ul li button:not(.${classDisabled})`
  );
  const opts: AddEventListenerOptions & EventListenerOptions = { passive: true };
  categories.forEach(cat => { 
    cat.removeEventListener('mouseenter', handlerGNaviCategoryOnMouseOver(settings, parentElement), opts);
    cat.removeEventListener('mouseleave', handlerGNaviCategoryOnMouseLeave(parentElement), opts);
    timeout = null;
  });
}

/** Detach handler - Removes scroll / resize events used to calculate offsets on fixed elements */
function detachGNaviHandlerScroll(parentElement: Element, targetElement: Element) {
  const opts: AddEventListenerOptions & EventListenerOptions = { passive: true };
  window.removeEventListener('scroll', handlerSetOffsetPosition(parentElement, targetElement), opts);
  window.removeEventListener('resize', handlerSetOffsetPosition(parentElement, targetElement), opts);
}

/** Triggers backdrop & selection mode */
const handlerGNaviCategoryOnClick = (settings: ISettings, parentElement: HTMLElement) => (e: Event) => {
  const { classContentCategories, classContentSubCategories, classOpen, classSelected, classContentSubcategory } = settings.gnavi as IGNavi;
  const currentTarget = (e.currentTarget as HTMLElement);
  const id = currentTarget.id;
  parentElement.dataset.gnaviBackdrop = 'true';
  parentElement.dataset.gnaviSelectionMode = 'true';
  const currentSubcategory = parentElement.querySelector(`.${classContentSubCategories} [data-gnavi-category-target="#${id}"]`);
  const prevSubcategory = parentElement.querySelector(`.${classContentSubCategories} [data-gnavi-category-target]:not([data-gnavi-category-target="#${id}"]).${classOpen}`);
  if (prevSubcategory) prevSubcategory.classList.remove(classOpen);
  if (currentSubcategory) currentSubcategory.classList.add(classOpen);
  const selectedCategories = parentElement.querySelectorAll(`.${classContentCategories} > ul > li.${classSelected}`);
  if (selectedCategories) selectedCategories.forEach((cat) => cat.classList.remove(classSelected));
  if (currentTarget.parentElement) currentTarget.parentElement.classList.add(classSelected);
  const subCategories = parentElement.querySelectorAll(`.${classContentSubCategories} > ul.${classContentSubcategory}`);
  if (subCategories) subCategories.forEach(subcat => deselectGNaviSubCategoryItem(settings, subcat)); 
  attachGNaviHandlerCategory(settings);
  toggleTooltips(settings, parentElement, TooltipMode.disabled);
  const categoryMenuElement = parentElement.querySelector(`.${classContentCategories}`);
  handlerSetOffsetPosition(parentElement, categoryMenuElement)(); // Set initial top offset
  attachGNaviHandlerScroll(parentElement, categoryMenuElement); // Attach scroll event
};

/** onMouseOver handler - Selecting Categories on hover */
const handlerGNaviCategoryOnMouseOver = (settings: ISettings, parentElement: Element) => (e: Event) => {
  if ((parentElement as HTMLElement).dataset?.gnaviSelectionMode !== 'true') return;
  if (timeout !== null) clearTimeout(timeout);
  timeout = setTimeout(() => {
    if(e.target) selectGNaviCategory(settings, e.target as Element);
  }, 100);
};

/** onMouseLeave handler - Clear timer for for the onMouseOver handler that is selecting Categories on hover */
const handlerGNaviCategoryOnMouseLeave = (parentElement: Element) => () => {
  if ((parentElement as HTMLElement).dataset?.gnaviSelectionMode !== 'true') return;
  if (timeout !== null) {
    clearTimeout(timeout);
    timeout = null;
  }
};

/** onClick handler - Open / close accordions inside Sub-category */
const handlerGNaviAccordionToggleOnClick = (settings: ISettings) => (e: Event) => {
  const { classContentSubcategoryAccordion, classOpen, classActive } = settings.gnavi as IGNavi;
  const currentTarget = (e.currentTarget as HTMLElement);
  if (!currentTarget) return;
  const accordion = currentTarget.closest('ul')?.querySelector(`.${classContentSubcategoryAccordion}.${classOpen}:not(.${classActive})`);
  accordion?.classList.remove(classOpen);
  const targetedAccordion = currentTarget.closest(`.${classContentSubcategoryAccordion}`);
  if (accordion !== targetedAccordion) {
    targetedAccordion?.classList.toggle(classOpen);
  }
};

/** onClick handler - Toggles between minimized / normal G-Navi */
const handlerGNaviTogglerOnClick = (settings: ISettings, parentElement: HTMLElement) => () => {
  const { labelTogglerTooltipTitleOpen, labelTogglerTooltipTitleClosed, classToggler } = settings.gnavi as IGNavi;
  const dataMinimized = parentElement.dataset.gnaviMinimized === 'true';
  parentElement.dataset.gnaviMinimized = (!dataMinimized).toString(); // Toggle status
  const togglerTarget = `.${classToggler} [data-gnavi-toggle="button"]`;
  const togglerElement = (parentElement.querySelector(togglerTarget) as HTMLElement);
  togglerElement.dataset.originalTitle = !dataMinimized ? labelTogglerTooltipTitleClosed : labelTogglerTooltipTitleOpen;
  showTooltips(settings, parentElement);
  toggleTooltips(settings, parentElement, TooltipMode.default, togglerTarget);
};

/** Calculate top & left position of a fixed element */
const handlerSetOffsetPosition = (parentElement: Element, targetElement: Element) => () => {
  if ((parentElement as HTMLElement).dataset?.gnaviSelectionMode !== 'true' || !targetElement) return;
  const rect = parentElement.getBoundingClientRect();
  (targetElement as HTMLElement).style.top = `${rect.top}px`;
  (targetElement as HTMLElement).style.left = `${rect.left}px`;
};

/** Calculate left position of a fixed element */
const handlerSetLeftPosition = (parentElement: Element, targetElement: Element) => () => {
  const rect = parentElement.getBoundingClientRect();
  (targetElement as HTMLElement).style.left = `${rect.left}px`;
};

/** Display Bootstrap tooltips  */
function showTooltips(settings: ISettings, parentElement: HTMLElement) {
  const dataMinimized = parentElement.dataset.gnaviMinimized === 'true';
  const secondDirectoryMode = parentElement.dataset.gnaviSecondDirectoryMode === 'true';
  const showTooltip = dataMinimized || secondDirectoryMode;
  if (showTooltip) {
    toggleTooltips(settings, parentElement, TooltipMode.enabled);
  } else {
    toggleTooltips(settings, parentElement, TooltipMode.disabled);
  }
}

/** Toggle Bootstrap Tooltips */
function toggleTooltips(settings: ISettings, parentElement: HTMLElement, mode: TooltipMode, target?: string) {
  const { classActive } = settings.gnavi as IGNavi;
  (($) => {
    const $this = $(parentElement);
    switch (mode) {
      case TooltipMode.disabled:
        try {
          $this.find('li[data-toggle="gnavi-tooltip"]').tooltip('dispose'); // Disable tooltips
        } catch (error) {
          console.error(error);
          console.error('Warning: Insert rms-ui.global JS before rms-ui.navi JS');
        }
        break;

      case TooltipMode.enabled:
        try {
          $this.find('li[data-toggle="gnavi-tooltip"]').filter(`:not(.${classActive})`)
            .tooltip({
              container: 'body',
              placement: 'right',
              trigger: 'hover',
              template: '<div class="tooltip rms-navi-global-tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div></div>',
            }); // (Re-)initialize tooltips
        } catch (error) {
          console.error(error);
          console.error('Warning: Insert rms-ui.global JS before rms-ui.navi JS');
        }
        break;

      default:
        if (!target) return;
        try {
          $this.find(target).tooltip('hide'); // Hide tooltips
          $this.find(target).filter(`:not(.${classActive})`)
            .tooltip({
              container: 'body', 
              placement: 'right', 
              trigger: 'hover',
              template: '<div class="tooltip rms-navi-global-tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div></div>',
            }); // (Re-)initialize tooltips
        } catch (error) {
          console.error(error);
          console.error('Warning: Insert rms-ui.global JS before rms-ui.navi JS');
        }
        break;
    }
  })(jQuery);
}

/** Sets Category to selected */
function selectGNaviCategory(settings: ISettings, targetElement: Element) {
  const { target, classContentCategories, classSelected } = settings.gnavi as IGNavi;
  const selectedCategories = document.querySelectorAll(`${target} .${classContentCategories} li.${classSelected}`);
  selectedCategories.forEach(selCat => selCat.classList.remove(classSelected));
  targetElement.closest('li')?.classList.add(classSelected);
  selectGNaviSubCategory(settings, targetElement.id);
}

/** Sets Subcategory to selected */
function selectGNaviSubCategory(settings: ISettings, id: string) {
  const { target, classContentSubCategories, classContentSubcategory, classOpen } = settings.gnavi as IGNavi;
  const openSubCategory = document.querySelector(`${target} .${classContentSubCategories} > ul.${classContentSubcategory}.${classOpen}`);
  openSubCategory?.classList.remove(classOpen);
  const selectedSubCategory = document.querySelector(`${target} .${classContentSubCategories} > ul.${classContentSubcategory}[data-gnavi-category-target="#${id}"]`);
  selectedSubCategory?.classList.add(classOpen);
}

/** Sets Subcategory Item to deselected */
function deselectGNaviSubCategoryItem(settings: ISettings, subcat: Element) {
  const { classContentSubcategoryAccordionMenu, classOpen, classActive } = settings.gnavi as IGNavi;
  Array.from(subcat.children).forEach((item) => {
    item.classList.remove(...[classOpen, classActive]); // Remove open & active class on accordionButton
    const activeAccItems = item.querySelectorAll(`.${classContentSubcategoryAccordionMenu} li.${classActive}`);
    activeAccItems.forEach(aai => aai.classList.remove(classActive)); // remove active class on accordion-menu item
  });
}

/** Resets the active Subcategory */
function resetGNaviActiveSubcategory(settings: ISettings, parentElement: Element) {
  const { target, gnavi } = settings;
  const { classOpen, classActive, classContentSubcategoryAccordion, classContentSubcategory } = gnavi as IGNavi;
  const accordionMenuItem = parentElement.querySelector(`.${classContentSubcategoryAccordion} a#${target}`);
  const openAccordionMenuItems = parentElement.querySelector(`.${classContentSubcategoryAccordion}.${classOpen}`);
  if (openAccordionMenuItems) openAccordionMenuItems.classList.remove(classOpen); // Remove open on Accordion Menu Item Buttons
  let subcategory: Element | null = null;
  const subcategories = parentElement.querySelector(`.${classContentSubcategory}.${classOpen}`);
  if (accordionMenuItem) {
    accordionMenuItem.closest('li')?.classList.add(classActive);
    const accordion = accordionMenuItem.closest(`li.${classContentSubcategoryAccordion}`);
    accordion?.classList.add(...[classOpen, classActive]);
    if (accordion) subcategory = accordion.closest(`.${classContentSubcategory}`);
  } else {
    const subcategoryItem = parentElement.querySelector(`.${classContentSubcategory} a#${target}`);
    if (subcategoryItem) {
      subcategory = subcategoryItem.closest(`.${classContentSubcategory}`);
      subcategoryItem.closest('li').classList.add(...[classOpen, classActive]);
    }
  }
  subcategories?.classList.remove(classOpen);
  subcategory?.classList.add(classOpen);
}

export interface IGNavi {
  target: string;
  classNames: string[],
  classActive: string;
  classDisabled: string;
  classOpen: string;
  classSelected: string;
  classIcon: string;
  classContentCategories: string;
  classContentCategoryItem: string;
  classContentSubCategories: string;
  classContentSubcategory: string;
  classContentSubcategoryOpen: string;
  classContentSubcategoryAccordion: string;
  classContentSubcategoryAccordionOpen: string;
  classContentSubcategoryAccordionMenu: string;
  classContentSubcategoryAccordionToggle: string;
  classContentSubcategoryItem: string;
  classContentSubcategoryItemNumber: string;
  classToggler: string;
  classBackdrop: string;
  classBackdropButton: string;
  classBackdropButtonContent: string;
  classDataError: string;
  labelTogglerTooltipTitleOpen: string;
  labelTogglerTooltipTitleClosed: string;
  labelBackdropButton: string;
  labelDataError: string;
}

enum TooltipMode {
  default = 'default',
  enabled = 'enabled',
  disabled = 'disabled',
}